import { $getSelectionStyleValueForProperty, $patchStyleText } from '@lexical/selection';
import { $getRoot, $getSelection, $isRangeSelection, BaseSelection, TextNode } from 'lexical';
import * as BrandDefinition from 'modules/BrandDefinition';
import * as Constants from 'const';
import * as Models from 'models';
import { fromPx, toPx } from 'utils/toPx';
import { $getTextNodeStyleValueForProperty, $patchTextNodeStyle } from '../../../lexicalUtils';
import { FontPluginStyle, getStyleObjectForBrandColor } from '../style';

export * from './charStyle';
export * from './fontSizeForBlockNode';
export * from './fontColor';
export * from './fontFamily';

// FONT BRAND COLOR

export function $getFontBrandColor(): BrandDefinition.BrandColorRef | undefined {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return undefined;
  }

  const HEX = $getSelectionStyleValueForProperty(selection, FontPluginStyle.BRAND_COLOR_HEX) || undefined;
  const name = $getSelectionStyleValueForProperty(selection, FontPluginStyle.BRAND_COLOR_NAME) || undefined;
  const tint = $getSelectionStyleValueForProperty(selection, FontPluginStyle.BRAND_COLOR_TINT) || undefined;

  if (!HEX) {
    return undefined;
  }

  return { HEX, name, tint: tint ? Number(tint) : undefined };
}

export function $applyFontBrandColorToAllNodes(
  colors: Models.BrandColorsList,
  colorRef: BrandDefinition.BrandColorRef | undefined,
): void {
  const style = getStyleObjectForBrandColor(colors, colorRef);
  $getRoot().getAllTextNodes()
    .forEach((node: TextNode) => $patchTextNodeStyle(node, style));
}

export function $doAllNodesHaveBrandColorName(name: string): boolean {
  const nodes = $getRoot().getAllTextNodes();

  return nodes.every((node) => {
    const nodeValue = $getTextNodeStyleValueForProperty(node, FontPluginStyle.BRAND_COLOR_NAME, '');

    return name === nodeValue;
  });
}

// BRAND FONT

export function $getBrandFont(): BrandDefinition.BrandFontRef | undefined {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return undefined;
  }

  const fontName = $getSelectionStyleValueForProperty(selection, FontPluginStyle.BRAND_FONT_NAME) || Constants.DefaultCustomStyle.BRANT_FONT_NAME;
  const characterStyleName = $getSelectionStyleValueForProperty(selection, FontPluginStyle.BRAND_FONT_CHARACTER_STYLE_NAME) || undefined;

  return { fontName, characterStyleName };
}

export function $patchFontWeight(selection: BaseSelection, isBold: boolean): void {
  $patchStyleText(selection, {
    [FontPluginStyle.FONT_WEIGHT]: isBold ? Constants.CharStyleValue.BOLD : Constants.CharStyleValue.REGULAR,
  });
}

export function $patchFontStyle(selection: BaseSelection, isNormal: boolean): void {
  $patchStyleText(selection, {
    [FontPluginStyle.FONT_STYLE]: isNormal ? Constants.CharStyleValue.NORMAL : Constants.CharStyleValue.ITALIC,
  });
}

export function $doAllNodesHaveBrandFontName(
  value: string,
  skipEmpty: boolean,
): boolean {
  const nodes = $getRoot().getAllTextNodes();
  if (!nodes.length) {
    return false;
  }

  return nodes.every((node) => {
    const nodeValue = $getTextNodeStyleValueForProperty(node, FontPluginStyle.BRAND_FONT_NAME, '');

    return value === nodeValue || (!nodeValue && skipEmpty);
  });
}

export function $applyBrandFontNameToAllNodes(value: string | undefined): void {
  const style = { [FontPluginStyle.BRAND_FONT_NAME]: value ?? null };
  $getRoot().getAllTextNodes().forEach(node => $patchTextNodeStyle(node, style));
}

export function $doAllNodesHaveBrandFontCharStyle(
  value: string,
  skipEmpty: boolean,
): boolean {
  const nodes = $getRoot().getAllTextNodes();
  if (!nodes.length) {
    return false;
  }

  return nodes.every((node) => {
    const nodeValue = $getTextNodeStyleValueForProperty(node, FontPluginStyle.BRAND_FONT_CHARACTER_STYLE_NAME, '');

    return nodeValue === value || (!nodeValue && skipEmpty);
  });
}

export function $applyBrandFontCharStyleToAllNodes(value: string | undefined): void {
  const style = { [FontPluginStyle.BRAND_FONT_CHARACTER_STYLE_NAME]: value ?? null };
  $getRoot().getAllTextNodes().forEach(node => $patchTextNodeStyle(node, style));
}

// FONT SIZE

export function $getFontSize(): number | undefined {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return undefined;
  }
  const value = $getSelectionStyleValueForProperty(selection, FontPluginStyle.FONT_SIZE);

  return value ? fromPx(value) : Constants.DefaultCustomStyle.FONT_SIZE;
}

export function $applyFontSizeToAllNodes(fontSize: number | undefined): void {
  const style = { [FontPluginStyle.FONT_SIZE]: toPx(fontSize) ?? null };
  $getRoot().getAllTextNodes().forEach((node) => {
    $patchTextNodeStyle(node, style);
  });
}

export function $doAllNodesHaveFontSize(
  value: number | undefined,
  skipEmpty: boolean,
): boolean {
  const nodes = $getRoot().getAllTextNodes();
  if (!nodes.length) {
    return false;
  }

  return nodes.every((node) => {
    const fontSizeWithPx = $getTextNodeStyleValueForProperty(node, FontPluginStyle.FONT_SIZE, '');
    if (!fontSizeWithPx && skipEmpty) {
      return true;
    }
    const fontSize = parseFloat(fontSizeWithPx);

    return value === fontSize;
  });
}
